// Frontend/src/components/BulkImportModal.tsx
import React from "react";
import Papa from "papaparse";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Upload,
  FileText,
  XCircle,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { listBuildings } from "@/api/buildings";
import { listUnits } from "@/api/units";
import { listResidents } from "@/api/residents";

type ImportType = "buildings" | "units" | "residents";

type PreviewRow = {
  id: string;
  rowNumber: number;
  data: Record<string, string>;
  errors: string[];
  valid: boolean;
};

/**
 * Expected CSV columns per import type.
 * Must match backend template exactly. Used for client-side pre-upload validation.
 */
const REQUIRED_COLUMNS: Record<ImportType, string[]> = {
  buildings: ["code", "name"],
  units: ["unit_code", "monthly_fee", "status"],
  residents: ["full_name", "email", "unit_code"],
};

const KNOWN_COLUMNS: Record<ImportType, string[]> = {
  buildings: [
    "code",
    "name",
    "address",
    "city",
    "state",
    "postal_code",
    "country",
    "total_units",
    "floors",
    "default_monthly_fee",
    "is_active",
    "notes",
  ],
  units: ["unit_code", "floor", "number", "monthly_fee", "initial_balance", "status"],
  residents: ["full_name", "email", "phone", "role", "unit_code", "language", "is_primary"],
};

/** Flexible result type for both success/failed rows */
type ImportResult = {
  success: boolean;
  total_processed: number;
  success_count: number;
  error_count: number;
  errors: Array<{
    row: number;
    field?: string;
    message?: string;
    errors?: string[];
    data?: Record<string, any>;
  }>;
  created_unit_ids?: string[];
  created_resident_ids?: string[];
};

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  type: ImportType;
  onImport: (file: File) => Promise<ImportResult>;
  onDownloadTemplate: () => Promise<string | void>;
  importing?: boolean;
}

const PRIMARY = "#1F3A93";
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

/** Utility: try to detect if an object looks like ImportResult */
function looksLikeImportResult(obj: any): obj is ImportResult {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.success === "boolean" &&
    typeof obj.total_processed === "number" &&
    typeof obj.success_count === "number" &&
    typeof obj.error_count === "number" &&
    Array.isArray(obj.errors)
  );
}

/** Normalize server error items to a single shape */
function normalizeResult(res: ImportResult): ImportResult {
  return {
    ...res,
    errors: (res.errors || []).map((e: any) => ({
      row: e.row,
      field: e.field,
      message:
        e.message ??
        (Array.isArray(e.errors) ? e.errors.join(", ") : undefined),
      errors: e.errors,
      data: e.data,
    })),
  };
}

/** Try to parse Axios error.response.data into ImportResult */
async function parseErrorToImportResult(err: any): Promise<ImportResult | null> {
  const data = err?.response?.data;

  // Already a plain object
  if (data && typeof data === "object" && !(data instanceof Blob)) {
    if (looksLikeImportResult(data)) return normalizeResult(data);
    return null;
  }

  // String (server sent text)
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (looksLikeImportResult(parsed)) return normalizeResult(parsed);
    } catch {
      /* ignore */
    }
    return null;
  }

  // Blob (browser)
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (looksLikeImportResult(parsed)) return normalizeResult(parsed);
    } catch {
      /* ignore */
    }
    return null;
  }

  return null;
}

function getFirstErrorMessage(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = getFirstErrorMessage(item);
      if (nested) return nested;
    }
  }
  if (value && typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const nested = getFirstErrorMessage(item);
      if (nested) return nested;
    }
  }
  return null;
}

function getImportModalErrorMessage(
  err: any,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  const message = String(err?.message ?? "");
  if (err?.code === "ECONNABORTED" || /timeout/i.test(message)) {
    return t("error.timeout");
  }
  if (!err?.response) {
    return t("error.network");
  }

  const status = Number(err?.response?.status ?? 0);
  if (status === 401) return t("error.unauthorized");
  if (status === 403) return t("error.forbidden");
  if (status === 404) return t("error.notFound");
  if (status === 429) return t("import.rateLimited");
  if (status >= 500) return t("error.serverError");

  const data = (err?.response?.data ?? null) as Record<string, unknown> | null;
  const rawMessage =
    getFirstErrorMessage(data?.details) ??
    getFirstErrorMessage(data?.file) ??
    getFirstErrorMessage(data?.error) ??
    getFirstErrorMessage(data?.detail) ??
    getFirstErrorMessage(data);

  if (rawMessage) {
    const normalized = rawMessage.toLowerCase();
    if (normalized.includes("utf-8")) return t("import.invalidEncoding");
    if (normalized.includes("5mb") || normalized.includes("too large") || normalized.includes("maximum")) {
      return t("import.fileTooLarge", { maxMb: 5 });
    }
    if (normalized.includes("csv") || normalized.includes("extension")) {
      return t("import.invalidFile");
    }
    return rawMessage;
  }

  return t("import.failed");
}

function normalizeRowData(headers: string[], row: any): Record<string, string> {
  const normalized: Record<string, string> = {};
  headers.forEach((h) => {
    normalized[h] = String(row?.[h] ?? "").trim();
  });
  return normalized;
}

function isBooleanLike(value: string): boolean {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  return ["true", "false", "1", "0", "yes", "no", "y", "n", "t", "f"].includes(normalized);
}

function validatePreviewRow(
  type: ImportType,
  data: Record<string, string>,
  t: (key: string, params?: Record<string, string | number>) => string
): string[] {
  const errors: string[] = [];

  for (const field of REQUIRED_COLUMNS[type]) {
    if (!data[field]) {
      errors.push(`${field}: ${t("validation.required")}`);
    }
  }

  if (type === "buildings") {
    if (data.total_units && (!Number.isFinite(Number(data.total_units)) || Number(data.total_units) < 0)) {
      errors.push(`total_units: ${t("error.invalidData")}`);
    }
    if (data.floors && (!Number.isFinite(Number(data.floors)) || Number(data.floors) < 1)) {
      errors.push(`floors: ${t("error.invalidData")}`);
    }
    if (
      data.default_monthly_fee &&
      (!Number.isFinite(Number(data.default_monthly_fee)) || Number(data.default_monthly_fee) < 0)
    ) {
      errors.push(`default_monthly_fee: ${t("error.invalidData")}`);
    }
    if (!isBooleanLike(data.is_active)) {
      errors.push(`is_active: ${t("error.invalidData")}`);
    }
  }

  if (type === "units") {
    if (data.monthly_fee && (!Number.isFinite(Number(data.monthly_fee)) || Number(data.monthly_fee) < 0)) {
      errors.push(`monthly_fee: ${t("error.invalidData")}`);
    }
    if (data.initial_balance && !Number.isFinite(Number(data.initial_balance))) {
      errors.push(`initial_balance: ${t("error.invalidData")}`);
    }
    const normalizedStatus = (data.status || "").toLowerCase();
    if (normalizedStatus && !["occupied", "vacant", "inactive"].includes(normalizedStatus)) {
      errors.push(`status: ${t("error.invalidData")}`);
    }
  }

  if (type === "residents") {
    if (data.email) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
      if (!emailOk) errors.push(`email: ${t("validation.email")}`);
    }
    const role = (data.role || "").toLowerCase();
    if (role && !["owner", "tenant"].includes(role)) {
      errors.push(`role: ${t("error.invalidData")}`);
    }
    const language = (data.language || "").toLowerCase();
    if (language && !["en", "es"].includes(language)) {
      errors.push(`language: ${t("error.invalidData")}`);
    }
    if (!isBooleanLike(data.is_primary)) {
      errors.push(`is_primary: ${t("error.invalidData")}`);
    }
  }

  return errors;
}

function duplicateKeyForRow(type: ImportType, data: Record<string, string>): string | null {
  if (type === "buildings") {
    const code = (data.code || "").trim().toLowerCase();
    return code ? `code:${code}` : null;
  }
  if (type === "units") {
    const unitCode = (data.unit_code || "").trim().toLowerCase();
    return unitCode ? `unit_code:${unitCode}` : null;
  }
  const email = (data.email || "").trim().toLowerCase();
  return email ? `email:${email}` : null;
}

async function getExistingDuplicateKeys(type: ImportType): Promise<Set<string>> {
  if (type === "buildings") {
    const buildings = await listBuildings();
    return new Set(buildings.map((b) => `code:${String(b.code ?? "").trim().toLowerCase()}`));
  }
  if (type === "units") {
    const units = await listUnits();
    return new Set(units.map((u) => `unit_code:${String(u.unit_code ?? "").trim().toLowerCase()}`));
  }
  const residents = await listResidents();
  return new Set(residents.map((r) => `email:${String(r.email ?? "").trim().toLowerCase()}`));
}

export default function BulkImportModal({
  open,
  onClose,
  type,
  onImport,
  onDownloadTemplate,
  importing = false,
}: BulkImportModalProps) {
  const { t } = useLanguage();
  const [file, setFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<ImportResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // CSV preview state
  const [parsing, setParsing] = React.useState(false);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<any[]>([]);
  const [previewRows, setPreviewRows] = React.useState<PreviewRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [confirmStep, setConfirmStep] = React.useState(false);
  const [submittedRows, setSubmittedRows] = React.useState<Record<string, string>[]>([]);

  const title =
    type === "buildings"
      ? t("import.entity.buildings")
      : type === "units"
      ? t("import.entity.units")
      : t("import.entity.residents");

  const resetPreview = () => {
    setParsing(false);
    setHeaders([]);
    setRows([]);
    setPreviewRows([]);
    setSelectedRowIds([]);
    setConfirmStep(false);
    setSubmittedRows([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;

    // Reset previous state
    setResult(null);
    setError(null);
    resetPreview();
    setFile(null);

    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      setError(t("import.invalidFile"));
      return;
    }

    setFile(selectedFile);

    // Parse for preview
    setParsing(true);
    Papa.parse(selectedFile, {
      header: true,
      comments: "#",
      skipEmptyLines: "greedy",
      dynamicTyping: false,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        setParsing(false);
        if (res.errors && res.errors.length > 0) {
          setError(res.errors[0]?.message || t("import.parseFailed"));
          return;
        }
        const data = (res.data as any[]) || [];
        const hdrs =
          (res.meta?.fields as string[]) ||
          (data.length ? Object.keys(data[0]) : []);

        // Client-side column validation
        const required = REQUIRED_COLUMNS[type];
        const known = KNOWN_COLUMNS[type];
        if (required) {
          const lower = hdrs.map((h) => h.toLowerCase());
          const missing = required.filter((col) => !lower.includes(col.toLowerCase()));
          const extra = hdrs.filter((h) => !known.map((c) => c.toLowerCase()).includes(h.toLowerCase()));
          if (missing.length > 0) {
            setError(
              t("import.missingColumns", { columns: missing.join(", ") })
            );
            setFile(null);
            return;
          }
          if (extra.length > 0) {
            // Warn but don't block — just set columns for preview
            console.warn("Extra CSV columns ignored:", extra);
          }
        }

        const preparedRows: PreviewRow[] = data.map((row, idx) => {
          const normalized = normalizeRowData(hdrs, row);
          const errors = validatePreviewRow(type, normalized, t);
          return {
            id: `${idx + 2}-${idx}`,
            rowNumber: idx + 2,
            data: normalized,
            errors,
            valid: errors.length === 0,
          };
        });

        const duplicateMap = new Map<string, number>();
        preparedRows.forEach((r) => {
          const key = duplicateKeyForRow(type, r.data);
          if (!key) return;
          duplicateMap.set(key, (duplicateMap.get(key) ?? 0) + 1);
        });

        const withDuplicates = preparedRows.map((r) => {
          const key = duplicateKeyForRow(type, r.data);
          const hasDuplicate = !!key && (duplicateMap.get(key) ?? 0) > 1;
          if (!hasDuplicate) return r;
          return {
            ...r,
            errors: [...r.errors, `${t("import.duplicateInFile")}: ${key?.split(":")[1] ?? ""}`],
            valid: false,
          };
        });

        const applyPreviewRows = (items: PreviewRow[]) => {
          setHeaders(hdrs);
          setRows(items.map((r) => r.data));
          setPreviewRows(items);
          setSelectedRowIds(items.filter((r) => r.valid).map((r) => r.id));
          setConfirmStep(false);
          setSubmittedRows([]);
        };

        applyPreviewRows(withDuplicates);

        void (async () => {
          try {
            const existingKeys = await getExistingDuplicateKeys(type);
            if (existingKeys.size === 0) return;
            const withExistingDuplicates = withDuplicates.map((r) => {
              const key = duplicateKeyForRow(type, r.data);
              if (!key || !existingKeys.has(key)) return r;
              return {
                ...r,
                errors: [...r.errors, `${t("import.duplicateExisting")}: ${key.split(":")[1] ?? ""}`],
                valid: false,
              };
            });
            applyPreviewRows(withExistingDuplicates);
          } catch {
            // Keep local preview usable even if precheck call fails.
          }
        })();
      },
      error: (err) => {
        setParsing(false);
        setError(err?.message || t("import.parseFailed"));
      },
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      setError(null);
      const response = await onDownloadTemplate();

      if (response) {
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err: any) {
      setError(getImportModalErrorMessage(err, t));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    const selectedValidRows = previewRows.filter(
      (r) => selectedRowIds.includes(r.id) && r.valid
    );
    if (selectedValidRows.length === 0) {
      setError(t("import.noRowsSelected"));
      return;
    }

    const selectedData = selectedValidRows.map((r) => r.data);
    const csvText = Papa.unparse({ fields: headers, data: selectedData });
    const importFile = new File([csvText], file.name, { type: "text/csv;charset=utf-8" });

    try {
      setError(null);
      setSubmittedRows(selectedData);
      const res = await onImport(importFile);
      const normalized = normalizeResult(res);
      setResult(normalized);

      // Clear file if fully successful
      if (normalized.success && normalized.error_count === 0) {
        setFile(null);
        resetPreview();
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err: any) {
      // Try to render server error payload instead of generic error
      const parsed = await parseErrorToImportResult(err);
      if (parsed) {
        setResult(parsed);
      } else {
        setError(getImportModalErrorMessage(err, t));
        setResult(null);
      }
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    resetPreview();
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  // Map server "row" -> original CSV row (server counts header as row 1)
  const getRowDataByServerRow = (serverRow: number) => {
    const idx = serverRow - 2; // data row index
    return submittedRows[idx] || rows[idx] || null;
  };

  const totalRows = previewRows.length;
  const validRows = previewRows.filter((r) => r.valid).length;
  const invalidRows = totalRows - validRows;
  const selectedValidCount = previewRows.filter(
    (r) => r.valid && selectedRowIds.includes(r.id)
  ).length;

  const selectAllValid = () => {
    setSelectedRowIds(previewRows.filter((r) => r.valid).map((r) => r.id));
  };

  const clearSelection = () => {
    setSelectedRowIds([]);
  };

  const toggleRowSelection = (id: string) => {
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Download an error report that merges server errors + original row data
  const downloadErrorReport = () => {
    if (!result || !result.errors?.length) return;

    const fields = ["row", "errors", ...headers];
    const data = result.errors.map((err) => {
      const rowData =
        err.data && Object.keys(err.data).length > 0
          ? err.data
          : getRowDataByServerRow(err.row) || {};
      const messages =
        err.message ??
        (Array.isArray(err.errors) ? err.errors.join("; ") : "") ??
        "";
      const record: Record<string, any> = {
        row: err.row,
        errors: messages,
      };
      headers.forEach((h) => {
        record[h] = rowData[h] ?? "";
      });
      return record;
    });

    const csv = Papa.unparse({ fields, data });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}_import_errors.csv`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="max-h-[90vh] overflow-hidden rounded-3xl bg-white/95 ring-1 ring-black/10 backdrop-blur-xl sm:max-w-[92vw] lg:max-w-[1100px] [&>div]:!p-0">
        <div className="flex max-h-[90vh] min-h-0 flex-col">
          <div className="shrink-0 border-b border-black/5 px-6 pb-4 pt-6">
            <DialogHeader>
              <DialogTitle className="text-xl">{t("import.title", { type: title })}</DialogTitle>
              <DialogDescription>
                {t("import.description", { type })}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 py-4">
            <div className="min-w-0 space-y-4">
          {/* Instructions */}
          <Alert className="rounded-2xl bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-900">
              <strong>{t("import.instructions")}</strong>
              <ol className="mt-2 ml-4 space-y-1 list-decimal">
                <li>{t("import.step1")}</li>
                <li>{t("import.step2", { type })}</li>
                <li>{t("import.step3")}</li>
                <li>{t("import.step4")}</li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Download Template */}
          <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="grid size-10 place-items-center rounded-xl bg-white ring-1 ring-black/10">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t("import.template", { type: title })}
                </div>
                <div className="text-xs text-gray-600">{t("import.csvFormat")}</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className={QUIET}
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              <Download className="h-4 w-4 mr-2" />
              {downloadingTemplate ? t("import.downloading") : t("import.download")}
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              {t("import.uploadLabel")}
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className={`flex-1 flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
                  file
                    ? "border-green-300 bg-green-50"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <Upload
                  className={`h-5 w-5 ${
                    file ? "text-green-600" : "text-gray-400"
                  }`}
                />
                <div className="flex-1">
                  <div
                    className={`text-sm font-medium ${
                      file ? "text-green-900" : "text-gray-700"
                    }`}
                  >
                    {file ? file.name : t("import.chooseFile")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {file
                      ? `${(file.size / 1024).toFixed(2)} KB`
                      : t("import.clickToBrowse")}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Progress (during import) */}
          {importing && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-900">
                {t("import.importing")}
              </div>
              <Progress value={50} className="h-2" />
              <div className="text-xs text-gray-600">
                {t("import.processingCsv")}
              </div>
            </div>
          )}

          {/* Parse status/errors */}
          {parsing && (
            <div className="text-sm text-gray-600">{t("import.readingPreview")}</div>
          )}

          {error && (
            <Alert variant="destructive" className="rounded-2xl">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* CSV Preview & Validation */}
          {!parsing && !error && previewRows.length > 0 && (
            <div className="min-w-0 space-y-3">
              <Alert className="rounded-2xl bg-slate-50 border-slate-200">
                <AlertCircle className="h-4 w-4 text-slate-700" />
                <AlertDescription className="text-slate-800 text-sm">
                  {t("import.noCreateUntilConfirm")}
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <div className="text-xs text-gray-500">{t("common.total")}</div>
                  <div className="text-base font-semibold text-gray-900">{totalRows}</div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="text-xs text-emerald-700">{t("import.validRows")}</div>
                  <div className="text-base font-semibold text-emerald-900">{validRows}</div>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
                  <div className="text-xs text-rose-700">{t("import.invalidRows")}</div>
                  <div className="text-base font-semibold text-rose-900">{invalidRows}</div>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
                  <div className="text-xs text-blue-700">{t("import.willCreate")}</div>
                  <div className="text-base font-semibold text-blue-900">{selectedValidCount}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" className={QUIET} onClick={selectAllValid}>
                  {t("import.selectAllValid")}
                </Button>
                <Button type="button" variant="outline" size="sm" className={QUIET} onClick={clearSelection}>
                  {t("import.clearSelection")}
                </Button>
              </div>

              {/* Unified cards preview for all screen sizes */}
              <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
                {previewRows.map((r) => {
                  const checked = selectedRowIds.includes(r.id);
                  return (
                    <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold text-gray-700">{t("import.rowHeader")} {r.rowNumber}</div>
                        <div className="flex items-center gap-2">
                          {r.valid ? (
                            <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-100 text-emerald-800">
                              {checked ? t("import.statusWillCreate") : t("import.statusSkipped")}
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-rose-100 text-rose-800">
                              {t("import.statusInvalid")}
                            </span>
                          )}
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!r.valid}
                            onChange={() => toggleRowSelection(r.id)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-700">
                        {headers.map((h) => (
                          <div key={h} className="break-all">
                            <span className="font-medium text-gray-600">{h}:</span>{" "}
                            {String(r.data[h] ?? "") || "-"}
                          </div>
                        ))}
                      </div>
                      {r.errors.length > 0 && (
                        <div className="rounded-lg bg-rose-50 border border-rose-200 px-2 py-1 text-xs text-rose-800">
                          {r.errors.join(" | ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {confirmStep && (
                <Alert className="rounded-2xl bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-700" />
                  <AlertDescription className="text-amber-900 text-sm">
                    {t("import.confirmPrompt", { count: selectedValidCount })}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Import Result */}
          {result && (
            <div className="space-y-3">
              {result.error_count === 0 ? (
                <Alert className="rounded-2xl bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-900">
                  <strong>{t("common.success")}!</strong> {t("import.success", { count: result.success_count, type })}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Alert className="rounded-2xl bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-900">
                      <strong>{t("import.partialFailed").split(":")[0]}:</strong>{" "}
                      {t("import.partialFailed", {
                        success: String(result.success_count),
                        total: String(result.total_processed),
                        type,
                        errors: String(result.error_count),
                      }).split(":").slice(1).join(":")}
                    </AlertDescription>
                  </Alert>

                  {/* Detailed errors with row data + download */}
                  {result.errors.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-red-900">
                          {t("import.errorDetails")}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className={QUIET}
                          onClick={downloadErrorReport}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t("import.downloadErrorCsv")}
                        </Button>
                      </div>

                      <div className="max-h-72 overflow-auto rounded-2xl border border-red-200 bg-red-50/50">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-red-100 text-red-900">
                            <tr>
                              <th className="text-left p-2 whitespace-nowrap">
                                {t("import.rowHeader")}
                              </th>
                              <th className="text-left p-2">{t("import.errorsHeader")}</th>
                              <th className="text-left p-2">{t("import.rowData")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.errors.map((err, idx) => {
                              const messages =
                                err.message ??
                                (Array.isArray(err.errors)
                                  ? err.errors.join(", ")
                                  : "") ??
                                "";
                              const rowData =
                                (err.data && Object.keys(err.data).length > 0
                                  ? err.data
                                  : getRowDataByServerRow(err.row)) || null;

                              return (
                                <tr key={idx} className="border-t border-red-200">
                                  <td className="p-2 align-top text-red-900 font-semibold">
                                    {err.row}
                                  </td>
                                  <td className="p-2 align-top text-red-800">
                                    {messages || "—"}
                                  </td>
                                  <td className="p-2 align-top">
                                    {rowData ? (
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                                        {headers.length > 0
                                          ? headers.map((h) => (
                                              <div
                                                key={h}
                                                className="text-red-900"
                                              >
                                                <span className="font-medium">
                                                  {h}:
                                                </span>{" "}
                                                <span className="text-red-800">
                                                  {String(rowData[h] ?? "")}
                                                </span>
                                              </div>
                                            ))
                                          : Object.entries(rowData).map(
                                              ([k, v]) => (
                                                <div
                                                  key={k}
                                                  className="text-red-900"
                                                >
                                                  <span className="font-medium">
                                                    {k}:
                                                  </span>{" "}
                                                    <span className="text-red-800">
                                                      {String(v ?? "")}
                                                    </span>
                                                </div>
                                              )
                                            )}
                                      </div>
                                    ) : (
                                      <span className="text-red-800 italic">
                                        {t("import.noData")}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
            </div>
          </div>

          <div className="shrink-0 border-t border-black/10 bg-white/95 px-6 py-4">
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose} className={QUIET}>
                {result && result.error_count === 0 ? t("common.close") : t("common.cancel")}
              </Button>
              {(!result || result.error_count > 0) && (
                <div className="flex items-center gap-2">
                  {confirmStep && (
                    <Button
                      type="button"
                      variant="outline"
                      className={QUIET}
                      onClick={() => setConfirmStep(false)}
                    >
                      {t("common.back")}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      if (!confirmStep) {
                        setConfirmStep(true);
                        return;
                      }
                      handleImport();
                    }}
                    disabled={!file || importing || selectedValidCount === 0}
                    className="rounded-xl text-white shadow-lg hover:shadow-xl"
                    style={{ background: PRIMARY }}
                  >
                    {importing
                      ? t("import.importing")
                      : confirmStep
                      ? t("common.confirm")
                      : t("import.reviewAndConfirm")}
                  </Button>
                </div>
              )}
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
